import { apiFetch, readErrorMessage } from '../api/client';
import { documentEditorHref } from './documentEditorUrls';
import type { DocumentEditorKind } from './documentFileTypes';
import { USER_STORAGE_MOUNT_ID } from '../storageFileAudit/formatters';
import {
  joinContentFolderPath,
  readDefaultContentFolderPath
} from '../fileBrowser/defaultContentFolderPrefs';
import { ensureUserStorageFolder } from '../fileBrowser/ensureUserStorageFolder';
import { mountApiBase } from '../fileBrowser/fileBrowserApi';

const MAX_NAME_ATTEMPTS = 50;

export type UploadedOfficeFile = {
  mountId: string;
  relativePath: string;
  fileName: string;
  editorKind: DocumentEditorKind;
  editorHref: string;
};

function splitFileName(fileName: string): { base: string; ext: string } {
  const trimmed = fileName.trim();
  const dot = trimmed.lastIndexOf('.');
  if (dot <= 0) return { base: trimmed || 'Upload', ext: '' };
  return { base: trimmed.slice(0, dot), ext: trimmed.slice(dot) };
}

/** Builds name.ext, then name (2).ext, … for My Storage uploads. */
export function nextUploadFileName(originalName: string, attempt: number): string {
  const { base, ext } = splitFileName(originalName || 'Upload');
  if (attempt <= 1) return `${base}${ext}`;
  return `${base} (${attempt})${ext}`;
}

/**
 * Uploads a local File to the signed-in user's My Storage default content folder
 * (auto-increment on conflict) and returns the editor URL for same-tab navigation.
 */
export async function uploadOfficeFileToUserStorage(
  file: File,
  editorKind: DocumentEditorKind,
  mountId: string = USER_STORAGE_MOUNT_ID,
  folderPath?: string
): Promise<UploadedOfficeFile> {
  const originalName =
    file.name?.trim() || (editorKind === 'spreadsheet' ? 'Workbook.xlsx' : 'Document.docx');
  const folder = await ensureUserStorageFolder(
    folderPath ?? readDefaultContentFolderPath(),
    mountId
  );
  let lastError = 'Could not upload file.';

  for (let attempt = 1; attempt <= MAX_NAME_ATTEMPTS; attempt++) {
    const fileName = nextUploadFileName(originalName, attempt);
    const form = new FormData();
    form.append('file', file, fileName);
    const uploadPath = folder ? encodeURIComponent(folder) : '';
    const res = await apiFetch(`${mountApiBase(mountId)}/upload?path=${uploadPath}`, {
      method: 'POST',
      body: form
    });

    if (res.ok) {
      const relativePath = joinContentFolderPath(folder, fileName);
      return {
        mountId,
        relativePath,
        fileName,
        editorKind,
        editorHref: documentEditorHref(editorKind, 'edit', mountId, relativePath)
      };
    }

    if (res.status === 409) {
      lastError = `A file named "${fileName}" already exists.`;
      continue;
    }

    throw new Error(await readErrorMessage(res));
  }

  throw new Error(lastError);
}
